import React, { useState, useRef, useEffect } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Animated,
  Platform,
  PanResponder,
  Dimensions,
  Image,
} from 'react-native';
import { COLORS } from '../constants/theme';
import authService from '../services/authService';

const { width: SCREEN_WIDTH } = Dimensions.get('window');

// Sidebar width responsive: más pequeña en laptops
const getSidebarWidth = () => {
  if (SCREEN_WIDTH >= 1600) return 280; // Desktop grande
  if (SCREEN_WIDTH >= 1280) return 240; // Laptop L
  return 200; // Laptop pequeña
};

const SIDEBAR_WIDTH = getSidebarWidth();
const SIDEBAR_COLLAPSED_WIDTH = 72;

// Export for other components
export { SIDEBAR_WIDTH, SIDEBAR_COLLAPSED_WIDTH };

interface SidebarProps {
  onNavigateToHome?: () => void;
  onNavigateToProducts?: () => void;
  onNavigateToEvents?: () => void;
  onNavigateToAnalytics?: () => void;
  onLogout?: () => void;
  currentScreen?: string;
  isOpen?: boolean;
  onToggle?: (isOpen: boolean) => void;
  isCollapsed?: boolean;
  onCollapsedChange?: (isCollapsed: boolean) => void;
}

export const Sidebar: React.FC<SidebarProps> = ({
  onNavigateToHome,
  onNavigateToProducts,
  onNavigateToEvents,
  onNavigateToAnalytics,
  onLogout,
  currentScreen = 'Home',
  isOpen: controlledIsOpen,
  onToggle,
  isCollapsed: controlledIsCollapsed,
  onCollapsedChange,
}) => {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const [internalIsCollapsed, setInternalIsCollapsed] = useState(false);
  const [userData, setUserData] = useState<any>(null);
  const isOpen = controlledIsOpen !== undefined ? controlledIsOpen : internalIsOpen;
  const isCollapsed = controlledIsCollapsed !== undefined ? controlledIsCollapsed : internalIsCollapsed;
  
  // Fetch user data
  useEffect(() => {
    const fetchUserProfile = async () => {
      const profile = await authService.getUserProfile();
      if (profile) {
        setUserData(profile);
      } else {
        const currentUser = authService.getCurrentUser();
        setUserData(currentUser);
      }
    };

    fetchUserProfile();
  }, []);
  
  // Inicializar translateX basado en el estado inicial de isOpen
  const translateX = useRef(
    new Animated.Value(controlledIsOpen !== undefined ? (controlledIsOpen ? 0 : -SIDEBAR_WIDTH) : -SIDEBAR_WIDTH)
  ).current;

  // Sincronizar animación cuando cambia isOpen desde el padre
  React.useEffect(() => {
    Animated.spring(translateX, {
      toValue: isOpen ? 0 : -SIDEBAR_WIDTH,
      useNativeDriver: true,
      tension: 65,
      friction: 11,
    }).start();
  }, [isOpen, translateX]);



  const toggleSidebar = () => {
    const newState = !isOpen;
    
    Animated.spring(translateX, {
      toValue: newState ? 0 : -SIDEBAR_WIDTH,
      useNativeDriver: true,
      tension: 65,
      friction: 11,
    }).start();
    
    if (onToggle) {
      onToggle(newState);
    } else {
      setInternalIsOpen(newState);
    }
  };

  // Pan responder para gestos de deslizamiento
  const panResponder = useRef(
    PanResponder.create({
      onStartShouldSetPanResponder: () => true,
      onMoveShouldSetPanResponder: (_, gestureState) => {
        return Math.abs(gestureState.dx) > 10;
      },
      onPanResponderMove: (_, gestureState) => {
        if (gestureState.dx < 0 && isOpen) {
          // Deslizar hacia la izquierda para cerrar
          translateX.setValue(Math.max(gestureState.dx, -SIDEBAR_WIDTH));
        } else if (gestureState.dx > 0 && !isOpen) {
          // Deslizar hacia la derecha para abrir
          translateX.setValue(Math.min(gestureState.dx - SIDEBAR_WIDTH, 0));
        }
      },
      onPanResponderRelease: (_, gestureState) => {
        if (Math.abs(gestureState.dx) > 50) {
          // Si deslizó más de 50px, cambiar estado
          if (gestureState.dx < 0 && isOpen) {
            toggleSidebar();
          } else if (gestureState.dx > 0 && !isOpen) {
            toggleSidebar();
          }
        } else {
          // Volver a la posición original
          Animated.spring(translateX, {
            toValue: isOpen ? 0 : -SIDEBAR_WIDTH,
            useNativeDriver: true,
          }).start();
        }
      },
    })
  ).current;

  const showAnalyticsMenu =
    authService.canAccessAdminAnalytics() && !!onNavigateToAnalytics;

  const menuItems = [
    {
      icon: require('../../assets/icons/home.png'),
      iconSelected: require('../../assets/icons/home-selected.png'),
      label: 'Home',
      onPress: onNavigateToHome,
      screen: 'Home',
    },
    {
      icon: require('../../assets/icons/portfolio.png'),
      iconSelected: require('../../assets/icons/portfolio-selected.png'),
      label: 'Portfolio',
      onPress: onNavigateToProducts,
      screen: 'Products',
    },
    {
      icon: require('../../assets/icons/events.png'),
      iconSelected: require('../../assets/icons/events-selected.png'),
      label: 'Events',
      onPress: onNavigateToEvents,
      screen: 'Events',
    },
    ...(showAnalyticsMenu
      ? [
          {
            icon: require('../../assets/icons/analytics.png'),
            iconSelected: require('../../assets/icons/analytics-selected.png'),
            label: 'Analytics',
            onPress: onNavigateToAnalytics,
            screen: 'Analytics',
          },
        ]
      : []),
  ];

  return (
    <>
      {/* Overlay en mobile y tablet cuando sidebar está abierta */}
      {isOpen && SCREEN_WIDTH < 1024 && (
        <TouchableOpacity
          style={styles.overlay}
          activeOpacity={1}
          onPress={toggleSidebar}
        />
      )}

      <Animated.View
        style={[
          styles.sidebar,
          {
            width: isCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH,
            transform: [{ translateX }],
          },
        ]}
        {...panResponder.panHandlers}
      >
        {/* Logo and Toggle */}
        <View style={[styles.logoContainer, isCollapsed && styles.logoContainerCollapsed]}>
          {!isCollapsed && (
            <>
              <Image 
                source={require('../../assets/images/logo.png')} 
                style={styles.logoImage}
                resizeMode="contain"
              />
              <Text style={styles.logoTitle}>Lifecycle</Text>
              <TouchableOpacity 
                style={styles.collapseButton}
                onPress={() => {
                  const newCollapsed = !isCollapsed;
                  if (controlledIsCollapsed !== undefined) {
                    onCollapsedChange?.(newCollapsed);
                  } else {
                    setInternalIsCollapsed(newCollapsed);
                  }
                }}
                activeOpacity={0.7}
              >
                <Text style={styles.collapseIcon}>‹</Text>
              </TouchableOpacity>
            </>
          )}
          {isCollapsed && (
            <>
              <Image 
                source={require('../../assets/images/logo.png')} 
                style={styles.logoImageCollapsed}
                resizeMode="contain"
              />
              <TouchableOpacity 
                style={styles.collapseButtonCollapsed}
                onPress={() => {
                  const newCollapsed = !isCollapsed;
                  if (controlledIsCollapsed !== undefined) {
                    onCollapsedChange?.(newCollapsed);
                  } else {
                    setInternalIsCollapsed(newCollapsed);
                  }
                }}
                activeOpacity={0.7}
              >
                <Text style={styles.collapseIcon}>›</Text>
              </TouchableOpacity>
            </>
          )}
        </View>

        {/* Menu Items */}
        <View style={styles.menuContainer}>
          {menuItems.map((item, index) => {
            const isActive = currentScreen === item.screen;
            
            return (
              <TouchableOpacity
                key={index}
                style={[
                  isCollapsed ? styles.menuItemCollapsed : styles.menuItem,
                  isActive && (isCollapsed ? styles.menuItemCollapsedActive : styles.menuItemActive),
                ]}
                onPress={() => {
                  item.onPress?.();
                  // Cerrar sidebar en mobile después de navegar
                  if (SCREEN_WIDTH < 768) {
                    toggleSidebar();
                  }
                }}
                activeOpacity={0.7}
              >
                <Image
                  source={isActive ? item.iconSelected : item.icon}
                  style={[styles.menuIconImage, isCollapsed && styles.menuIconImageCollapsed]}
                  resizeMode="contain"
                />
                {!isCollapsed && (
                  <Text
                    style={[
                      styles.menuLabel,
                      isActive && styles.menuLabelActive,
                    ]}
                    numberOfLines={1}
                  >
                    {item.label}
                  </Text>
                )}
              </TouchableOpacity>
            );
          })}


        </View>

        {/* User Section */}
        <View style={styles.bottomSection}>
          <View style={[styles.userSection, isCollapsed && styles.userSectionCollapsed]}>
            <View style={styles.userAvatar}>
              <Text style={styles.userAvatarText}>
                {userData?.name ? userData.name.charAt(0).toUpperCase() : userData?.email?.charAt(0).toUpperCase() || 'U'}
              </Text>
            </View>
            {!isCollapsed && (
              <View style={styles.userInfo}>
                <Text style={styles.userName}>
                  {userData?.name || userData?.email || 'User'}
                </Text>
              </View>
            )}
          </View>
          
          {/* Logout Button */}
          <TouchableOpacity
            style={[styles.logoutButton, isCollapsed && styles.logoutButtonCollapsed]}
            onPress={onLogout}
            activeOpacity={0.7}
          >
            {!isCollapsed ? (
              <Text style={styles.logoutText}>Log out</Text>
            ) : (
              <Text style={styles.logoutIcon}>⏻</Text>
            )}
          </TouchableOpacity>
        </View>
      </Animated.View>
    </>
  );
};

const styles = StyleSheet.create({
  overlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 9999,
  },
  sidebar: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    width: SIDEBAR_WIDTH, // This will be overridden by inline style
    height: '100vh',
    backgroundColor: '#FFFFFF',
    borderTopRightRadius: SCREEN_WIDTH >= 768 ? 20 : 0,
    borderBottomRightRadius: SCREEN_WIDTH >= 768 ? 20 : 0,
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    zIndex: 10000,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    ...Platform.select({
      web: {
        // @ts-ignore - fixed is valid for web
        position: SCREEN_WIDTH >= 768 ? 'fixed' : 'absolute',
        boxShadow: SCREEN_WIDTH >= 768 ? '4px 0 20px rgba(0, 0, 0, 0.08)' : '2px 0 8px rgba(0, 0, 0, 0.1)',
      },
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 4, height: 0 },
        shadowOpacity: 0.08,
        shadowRadius: 20,
        elevation: 8,
      },
    }),
  } as any,
  logoContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 24,
    paddingTop: 32,
    paddingBottom: 28,
  },
  logoContainerCollapsed: {
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 12,
  },
  logoImage: {
    width: 40,
    height: 40,
    marginRight: 12,
  },
  logoImageCollapsed: {
    width: 32,
    height: 32,
  },
  logoTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#1F2937',
    fontFamily: 'Montserrat_600SemiBold',
    flex: 1,
  },
  collapseButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#F3F4F6',
    alignItems: 'center',
    justifyContent: 'center',
  },
  collapseButtonCollapsed: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#F3F4F6',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 12,
  },
  collapseIcon: {
    fontSize: 20,
    color: '#6B7280',
    fontWeight: '600',
  },
  menuContainer: {
    flex: 1,
    paddingHorizontal: 16,
    paddingTop: 8,
    overflow: 'hidden',
  },
  menuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 12,
    marginBottom: 8,
  },
  menuItemActive: {
    backgroundColor: '#F0F9FF',
  },
  menuItemCollapsed: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    paddingHorizontal: 8,
    borderRadius: 12,
    marginBottom: 8,
  },
  menuItemCollapsedActive: {
    backgroundColor: '#F0F9FF',
  },
  menuIconImage: {
    width: 24,
    height: 24,
    marginRight: 14,
  },
  menuIconImageCollapsed: {
    marginRight: 0,
    width: 26,
    height: 26,
  },
  menuLabel: {
    fontSize: 15,
    color: '#9CA3AF',
    fontWeight: '500',
    fontFamily: 'Montserrat_500Medium',
  },
  menuLabelActive: {
    color: '#1F2937',
    fontWeight: '600',
    fontFamily: 'Montserrat_600SemiBold',
  },

  userSection: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 20,
    marginHorizontal: 16,
    marginBottom: 16,
  },
  userSectionCollapsed: {
    justifyContent: 'center',
    paddingHorizontal: 8,
  },
  userAvatar: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: '#D1D5DB',
    alignItems: 'center',
    justifyContent: 'center',
  },
  userAvatarText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#6B7280',
    fontFamily: 'Montserrat_600SemiBold',
  },
  userInfo: {
    marginLeft: 12,
    flex: 1,
  },
  userName: {
    fontSize: 14,
    fontWeight: '500',
    color: '#6B7280',
    fontFamily: 'Montserrat_500Medium',
  },
  bottomSection: {
    paddingBottom: 8,
  },
  logoutButton: {
    paddingVertical: 12,
    paddingHorizontal: 36,
    marginHorizontal: 16,
    marginBottom: 8,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  logoutButtonCollapsed: {
    paddingHorizontal: 8,
  },
  logoutText: {
    fontSize: 15,
    fontWeight: '600',
    color: '#EF4444',
    fontFamily: 'Montserrat_600SemiBold',
  },
  logoutIcon: {
    fontSize: 24,
    color: '#EF4444',
  },
});
